/**
 * One set of breakpoints for the whole app.
 *
 * The repo used to name the same edges four different ways (599/600, 767/768,
 * 959/960, 1279/1280) and MUI's own `down('sm')` means "below 960", which is how
 * an iPad in portrait ended up with the phone layout. Everything responsive reads
 * from here instead:
 *
 * - phone   — below 768px. Single column, bottom tab bar, card lists, sheets.
 * - tablet  — 768 to 959px. Collapsed icon rail, desktop tables.
 * - desktop — 960px and up.
 *
 * V2 already drew its phone edge at 768px, so this matches it.
 */
export const BREAKPOINTS = { phone: 768, tablet: 960, desktop: 1280 }

// Strings for window.matchMedia / useMediaQuery.
export const QUERY = {
	phone: `(max-width: ${BREAKPOINTS.phone - 0.02}px)`,
	tabletDown: `(max-width: ${BREAKPOINTS.tablet - 0.02}px)`,
	tablet: `(min-width: ${BREAKPOINTS.phone}px) and (max-width: ${BREAKPOINTS.tablet - 0.02}px)`,
	touch: '(hover: none) and (pointer: coarse)',
	// A real pointer that can hover. Hover-only looks (a link's underline) go here, or a
	// tap on a phone leaves them stuck on the last thing touched (SSI-458).
	hover: '(hover: hover)',
	// A phone turned sideways: plenty of width, almost no height.
	shortLandscape: '(max-height: 500px) and (orientation: landscape)',
}

// The same edges as CSS media rules, for styled-components / makeStyles keys.
export const MEDIA = {
	phone: `@media ${QUERY.phone}`,
	tabletDown: `@media ${QUERY.tabletDown}`,
	tablet: `@media ${QUERY.tablet}`,
	touch: `@media ${QUERY.touch}`,
	hover: `@media ${QUERY.hover}`,
	shortLandscape: `@media ${QUERY.shortLandscape}`,
}

// The smallest comfortable tap area (Apple HIG 44pt, Material 48dp).
export const TOUCH_TARGET = 44

export const SAFE_AREA = {
	top: 'env(safe-area-inset-top, 0px)',
	bottom: 'env(safe-area-inset-bottom, 0px)',
}

// Fixed chrome on phones, so pages can clear it without guessing.
export const MOBILE_HEADER_HEIGHT = 56
export const MOBILE_TAB_BAR_HEIGHT = 60

/**
 * A height relative to the visible viewport. iOS Safari's `100vh` is the height
 * with its toolbars hidden, so anything sized to it has its bottom (a composer, a
 * Save button) under the toolbar. `dvh` tracks what is actually visible; `vh`
 * stays as the fallback for browsers without it.
 *
 * styled-components: `${viewportHeight('120px')}` → two declarations.
 */
export const viewportHeight = (offset = '0px', property = 'height') => `
	${property}: calc(100vh - ${offset});
	${property}: calc(100dvh - ${offset});
`

// The same for JSS (makeStyles), which takes fallbacks as an array.
export const viewportHeightJss = (offset = '0px', property = 'height') => ({
	[property]: `calc(100dvh - ${offset})`,
	fallbacks: [{ [property]: `calc(100vh - ${offset})` }],
})
